/**
 * Permission API Adapter
 * 提供 V1 到 V2 的平滑遷移
 */

import { permissionApi as permissionApiV1 } from './userApi';
import { permissionApiV2, migrationHelper } from './userApiV2';
import { 
  PermissionGroup, 
  PermissionGroupCreateRequest,
  PermissionGroupUpdateRequest,
  UserPermissionsRequest,
  SuccessResponse 
} from '../types/user';

// 功能開關：控制使用 V1 還是 V2 API
const USE_V2_API = {
  // 權限群組管理
  getPermissionGroups: true,
  getPermissionGroup: true,
  createPermissionGroup: true,
  updatePermissionGroup: true,
  deletePermissionGroup: true,
  
  // 群組成員管理
  getGroupMembers: true,
  addGroupMembers: true,
  removeGroupMembers: true,
  
  // 群組權限管理
  getGroupPermissions: true,
  setGroupPermissions: true,
  
  // 使用者權限管理
  getUserPermissions: true, // 使用 V2 API
  setUserPermissions: true,
  getUserEffectivePermissions: true,
  getCurrentUserPermissions: true,
  getUserPermissionMatrix: true,
  
  // 程式管理
  getPrograms: true, // 使用 V2 API
  getProgramTree: true,
  
  // 權限檢查
  checkPermission: true,
  checkMultiplePermissions: true,
  
  // 權限分析
  getUserPermissionAnalysis: true
};

/**
 * 統一的權限 API 介面
 * 根據功能開關自動選擇 V1 或 V2 API
 */
export const permissionApiAdapter = {
  // === 權限群組管理 ===
  
  async getPermissionGroups(params?: { 
    page?: number; 
    limit?: number; 
    search?: string; 
    isActive?: boolean 
  }): Promise<SuccessResponse<{ items: PermissionGroup[]; pagination: any }>> {
    if (USE_V2_API.getPermissionGroups) {
      return permissionApiV2.getPermissionGroups(params);
    }
    return permissionApiV1.getPermissionGroups(params);
  },

  async getPermissionGroup(groupId: string): Promise<SuccessResponse<PermissionGroup>> {
    if (USE_V2_API.getPermissionGroup) {
      const response = await permissionApiV2.getPermissionGroup(groupId);
      // 轉換權限格式：pgId -> programId
      if (response.data.permissions) {
        response.data.permissions = response.data.permissions.map((perm: any) => ({
          programId: perm.pgId,
          programName: perm.programName || '',
          addNew: perm.addNew || false,
          edit: perm.edit || false,
          delete: perm.del || false,  // 後端使用 del
          export: perm.export || false,
          print: perm.printer || false,  // 後端使用 printer
          query: perm.query || false,
          approve: perm.approve ?? false
        }));
      }
      return response;
    }
    // V1 API 沒有單獨獲取群組的端點，需要從列表中找
    const response = await permissionApiV1.getPermissionGroups();
    const group = response.data.items.find(g => g.rGroupId === groupId);
    if (!group) {
      throw new Error('權限群組不存在');
    }
    return {
      success: true,
      data: group,
      message: '獲取成功',
      timestamp: new Date().toISOString()
    };
  },

  async createPermissionGroup(groupData: PermissionGroupCreateRequest): Promise<SuccessResponse<PermissionGroup>> {
    if (USE_V2_API.createPermissionGroup) {
      return permissionApiV2.createPermissionGroup(groupData);
    }
    return permissionApiV1.createPermissionGroup(groupData);
  },

  async updatePermissionGroup(groupId: string, groupData: PermissionGroupUpdateRequest): Promise<SuccessResponse<PermissionGroup>> {
    if (USE_V2_API.updatePermissionGroup) {
      return permissionApiV2.updatePermissionGroup(groupId, groupData);
    }
    // V1 API 需要適配
    throw new Error('V1 API 不支援更新權限群組');
  },

  async deletePermissionGroup(groupId: string): Promise<SuccessResponse<void>> {
    if (USE_V2_API.deletePermissionGroup) {
      return permissionApiV2.deletePermissionGroup(groupId);
    }
    // V1 API 需要適配
    throw new Error('V1 API 不支援刪除權限群組');
  },

  // === 群組成員管理 ===

  async getGroupMembers(groupId: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
  }) {
    if (USE_V2_API.getGroupMembers) {
      return permissionApiV2.getGroupMembers(groupId, params);
    }
    // V1 API 需要適配
    throw new Error('V1 API 不支援獲取群組成員');
  },

  async addGroupMembers(groupId: string, userIds: string[]): Promise<SuccessResponse<void>> {
    if (USE_V2_API.addGroupMembers) {
      return permissionApiV2.addGroupMembers(groupId, userIds);
    }
    // V1 API 需要適配
    throw new Error('V1 API 不支援添加群組成員');
  },

  async removeGroupMembers(groupId: string, userIds: string[]): Promise<SuccessResponse<void>> {
    if (USE_V2_API.removeGroupMembers) {
      return permissionApiV2.removeGroupMembers(groupId, userIds);
    }
    // V1 API 需要適配
    throw new Error('V1 API 不支援移除群組成員');
  },

  // === 群組權限管理 ===

  async getGroupPermissions(groupId: string) {
    if (USE_V2_API.getGroupPermissions) {
      return permissionApiV2.getGroupPermissions(groupId);
    }
    // V1 API 需要適配
    throw new Error('V1 API 不支援獲取群組權限');
  },

  async setGroupPermissions(groupId: string, permissions: UserPermissionsRequest): Promise<SuccessResponse<void>> {
    if (USE_V2_API.setGroupPermissions) {
      return permissionApiV2.setGroupPermissions(groupId, permissions);
    }
    // V1 API 需要適配
    throw new Error('V1 API 不支援設定群組權限');
  },

  // === 使用者權限管理 ===

  async getUserPermissions(userId: string) {
    if (USE_V2_API.getUserPermissions) {
      const response = await permissionApiV2.getUserPermissions(userId);
      // V2 API 返回的是 UserPermissionInfo，包含 personalPermissions, groupPermissions, effectivePermissions
      // 前端需要的是個人權限數組，所以提取 personalPermissions
      const responseData = response.data as any; // 使用 any 型別來避免 TypeScript 錯誤
      console.log('V2 API Response Data:', responseData);
      console.log('Personal Permissions:', responseData.personalPermissions);
      
      const convertedPermissions = migrationHelper.convertV2PermissionsToV1(responseData.personalPermissions || []);
      console.log('Converted Permissions:', convertedPermissions);
      
      return {
        ...response,
        data: {
          userId: responseData.userId || '',
          userName: responseData.userName || '',
          permissions: convertedPermissions
        }
      };
    }
    return permissionApiV1.getUserPermissions(userId);
  },

  async setUserPermissions(userId: string, permissions: UserPermissionsRequest) {
    if (USE_V2_API.setUserPermissions) {
      // 轉換 V1 格式到 V2 格式
      const v2Permissions = {
        permissions: migrationHelper.convertV1PermissionsToV2(permissions.permissions)
      };
      return permissionApiV2.setUserPermissions(userId, v2Permissions);
    }
    return permissionApiV1.setUserPermissions(userId, permissions);
  },

  async getUserEffectivePermissions(userId: string) {
    if (USE_V2_API.getUserEffectivePermissions) {
      return permissionApiV2.getUserEffectivePermissions(userId);
    }
    return permissionApiV1.getUserEffectivePermissions(userId);
  },

  async getCurrentUserPermissions() {
    if (USE_V2_API.getCurrentUserPermissions) {
      return permissionApiV2.getCurrentUserPermissions();
    }
    // V1 API 使用 getProfile
    const { authApi } = await import('./userApi');
    const profile = await authApi.getProfile();
    return {
      success: true,
      data: {
        userId: profile.data.userId,
        userName: profile.data.userName,
        permissions: profile.data.permissions || []
      },
      message: '獲取成功',
      timestamp: new Date().toISOString()
    };
  },

  async getUserPermissionMatrix(userId: string) {
    if (USE_V2_API.getUserPermissionMatrix) {
      return permissionApiV2.getUserPermissionMatrix(userId);
    }
    // V1 API 不支援
    throw new Error('V1 API 不支援權限矩陣功能');
  },

  // === 程式管理 ===

  async getPrograms(params?: {
    active?: boolean;
    page?: number;
    limit?: number;
    search?: string;
  }) {
    if (USE_V2_API.getPrograms) {
      // V2 使用分頁，需要適配參數
      const v2Params = {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        isActive: params?.active
      };
      return permissionApiV2.getPrograms(v2Params);
    }
    // V1 API 返回數組，需要包裝成分頁格式
    const response = await permissionApiV1.getPrograms(params);
    return {
      ...response,
      data: {
        items: response.data,
        pagination: {
          page: 1,
          limit: response.data.length,
          total: response.data.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false
        }
      }
    };
  },

  async getProgramTree() {
    if (USE_V2_API.getProgramTree) {
      return permissionApiV2.getProgramTree();
    }
    // V1 API 需要手動構建樹狀結構
    const response = await permissionApiV1.getPrograms();
    const programs = response.data;
    
    // 構建樹狀結構
    const buildTree = (items: any[], parentId?: number): any[] => {
      return items
        .filter(item => item.pgParent === parentId)
        .map(item => ({
          ...item,
          children: buildTree(items, item.pgId)
        }));
    };
    
    return {
      success: true,
      data: buildTree(programs, undefined),
      message: '獲取成功',
      timestamp: new Date().toISOString()
    };
  },

  // === 權限檢查 ===

  async checkPermission(userId: string, programId: number, permission: string) {
    if (USE_V2_API.checkPermission) {
      return permissionApiV2.checkPermission(userId, programId, permission);
    }
    // V1 API 需要從有效權限中查找
    const response = await permissionApiV1.getUserEffectivePermissions(userId);
    const userPermissions = response.data.effectivePermissions || [];
    const programPerm = userPermissions.find((p: any) => p.programId === programId);
    
    let hasPermission = false;
    if (programPerm) {
      switch (permission) {
        case 'addNew': hasPermission = programPerm.addNew; break;
        case 'edit': hasPermission = programPerm.edit; break;
        case 'del': 
        case 'delete': hasPermission = programPerm.delete; break;
        case 'export': hasPermission = programPerm.export; break;
        case 'printer':
        case 'print': hasPermission = programPerm.print; break;
        case 'query': hasPermission = programPerm.query; break;
      }
    }
    
    return {
      success: true,
      data: {
        hasPermission,
        userId,
        programId,
        permission
      },
      message: '檢查完成',
      timestamp: new Date().toISOString()
    };
  },

  async checkMultiplePermissions(checks: {
    userId: string;
    checks: Array<{
      programId: number;
      permission: string;
    }>;
  }) {
    if (USE_V2_API.checkMultiplePermissions) {
      return permissionApiV2.checkMultiplePermissions(checks);
    }
    // V1 API 需要逐個檢查
    const results = await Promise.all(
      checks.checks.map(async (check) => {
        const response = await this.checkPermission(checks.userId, check.programId, check.permission);
        return {
          programId: check.programId,
          permission: check.permission,
          hasPermission: response.data.hasPermission
        };
      })
    );
    
    return {
      success: true,
      data: { results },
      message: '檢查完成',
      timestamp: new Date().toISOString()
    };
  },

  // === 權限分析 ===

  async getUserPermissionAnalysis(userId: string) {
    if (USE_V2_API.getUserPermissionAnalysis) {
      return permissionApiV2.getUserPermissionAnalysis(userId);
    }
    // V1 API 不支援
    throw new Error('V1 API 不支援權限分析功能');
  }
};

// 導出適配器作為預設
export default permissionApiAdapter;